import React, {
  forwardRef,
  RefObject,
  useId,
  useImperativeHandle,
  useRef,
} from 'react';

import { useOnClickOutside } from '../../../hooks';
import { SelectOption, SelectValue } from './select.interface';

export type DropdownRefs = {
  listContainerRef?: RefObject<HTMLDivElement | null>;
  listRef?: RefObject<HTMLUListElement | null>;
};

interface DropdownProps {
  openList: boolean;
  type: string;
  defaultValue: string;
  options: SelectValue[];
  handleValue: (selectedOption: SelectOption) => void;
  handleKeyDown: (e: React.KeyboardEvent<HTMLLIElement>) => void;
  className?: string;
  addToRef?: (el: HTMLLIElement) => void;
  searchAll?: () => void;
  setOpenList: (x: boolean) => void;
}

export const Dropdown = forwardRef<DropdownRefs, DropdownProps>(
  (props, ref) => {
    const listRef = useRef<HTMLUListElement>(null);
    const listContainerRef = useRef<HTMLDivElement>(null);

    const {
      openList,
      type,
      defaultValue,
      options,
      handleValue,
      handleKeyDown,
      className,
      addToRef,
      searchAll,
      setOpenList,
    } = props;

    useImperativeHandle(ref, () => ({
      listRef,
      listContainerRef,
    }));

    useOnClickOutside(listContainerRef, () => setOpenList(false));

    const handleOnClick = (e: React.MouseEvent<HTMLLIElement>): void => {
      const selectedOption: SelectOption = {
        optionText: e.currentTarget.innerText,
        optionValue: e.currentTarget.dataset.optionValue ?? '',
      };

      handleValue(selectedOption);
    };

    const dropdownId = useId();
    return (
      <>
        <div
          aria-expanded="false"
          aria-controls={dropdownId}
          ref={listContainerRef}
          className={
            openList && type === 'primary'
              ? `absolute -top-px -left-px -right-px z-10 select-none rounded-xl border border-brand-grey-2 bg-brand-white py-1 pr-3 outline outline-3 outline-offset-3 outline-text-light ${className}`
              : openList && type === 'secondary'
                ? 'h-auto w-full '
                : 'hidden w-full'
          }
        >
          <ul
            id={dropdownId}
            role="list"
            ref={listRef}
            className={
              openList && type === 'primary'
                ? 'scrollbar-mt max-h-60 w-full overflow-auto'
                : openList && type === 'secondary'
                  ? 'mt-2 max-h-[220px] w-full overflow-auto scrollbar-default '
                  : 'hidden max-h-0 w-full '
            }
          >
            <li
              key="default-all-option"
              onClick={searchAll}
              className=" w-full whitespace-normal px-4 pt-1.5 pb-2 font-sans text-bodyl font-normal text-text-light "
            >
              {defaultValue}
            </li>
            {options.map((value, index) => {
              // Create a unique key using id if available, otherwise use index and optionValue
              const uniqueKey =
                value.id || `option-${index}-${value?.value?.optionValue}`;

              return (
                <li
                  id={value.id}
                  key={uniqueKey}
                  ref={addToRef}
                  role="listitem"
                  className="w-full whitespace-normal py-2 px-4 font-sans text-bodymheavy text-icon-default lg:hover:w-[98%] lg:hover:rounded-lg lg:hover:bg-neutral-grey-100"
                  tabIndex={0}
                  onClick={(e) => handleOnClick(e)}
                  onKeyDown={handleKeyDown}
                  data-option-value={value.value.optionValue}
                >
                  {value.value.optionText}
                </li>
              );
            })}
          </ul>
        </div>
      </>
    );
  },
);

Dropdown.displayName = 'Dropdown';
